import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Shared body for the two light-surface legal pages. Renders:
 *   - An optional "Draft — pending legal review" badge banner (default on)
 *   - The page H1 in dark text
 *   - An optional "Last updated: …" metadata line under the H1
 *   - A two-column layout: sticky section index in the left rail (>= lg)
 *     and the prose (max-w 72ch) on the right
 *
 * Section shape:
 *   { id, heading, tocLabel?, body }
 * If `tocLabel` is provided it's used in the left-rail; otherwise `heading`
 * is used verbatim.
 */
export function LegalPage({
  title,
  sections,
  testId,
  lastUpdated,
  dateLine,
  intro,
  notice,
  draftBanner = true,
}) {
  return (
    <article
      className="container-frac py-16 lg:py-24"
      data-testid={testId ?? "legal-page"}
    >
      {draftBanner && (
        <div
          className="mb-10 inline-flex items-center gap-2 rounded-full border border-[#B58900]/40 bg-[#FFF6D6] px-3 py-1.5 text-xs font-semibold text-[#7A5A00]"
          role="status"
          data-testid="legal-draft-banner"
        >
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} />
          Draft — pending legal review
        </div>
      )}

      <h1
        className="max-w-3xl text-balance text-4xl font-bold leading-tight tracking-tight text-frac-black sm:text-5xl"
        data-testid="page-h1"
      >
        {title}
      </h1>

      {dateLine ? (
        <p
          className="mt-4 text-sm text-frac-gray-50"
          style={{ fontWeight: 400 }}
          data-testid="legal-last-updated"
        >
          {dateLine}
        </p>
      ) : (
        lastUpdated && (
          <p
            className="mt-4 text-sm text-frac-gray-50"
            style={{ fontWeight: 400 }}
            data-testid="legal-last-updated"
          >
            Last updated: {lastUpdated}
          </p>
        )
      )}

      {notice && (
        <div
          className="measure-legal mt-6"
          role="note"
          data-testid="legal-notice"
          style={{
            background: "#FBF0DC",
            borderLeft: "3px solid #8A5A00",
            color: "#8A5A00",
            padding: "12px 16px",
            borderRadius: "var(--radius-sm)",
            fontSize: "14px",
            lineHeight: "1.55",
            fontWeight: 500,
          }}
        >
          {notice}
        </div>
      )}

      {intro && (
        <div
          className="measure-legal mt-8 space-y-4 text-base leading-normal text-frac-gray-70"
          data-testid="legal-intro"
        >
          {intro}
        </div>
      )}

      <div className="mt-14 grid gap-14 lg:grid-cols-[220px_1fr]">
        <aside className="hidden lg:block" aria-label="Section index">
          <nav className="sticky top-28">
            <div className="text-xs font-semibold uppercase tracking-caps text-frac-purple-70">
              On this page
            </div>
            <ul
              className="mt-4 space-y-2 border-l border-frac-purple/20 pl-4"
              data-testid="legal-toc"
            >
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="block text-sm text-frac-purple-70 transition-colors duration-fast ease-out hover:text-frac-purple"
                    data-testid={`legal-toc-${s.id}`}
                  >
                    {s.tocLabel ?? s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <div className="measure-legal space-y-14 text-base leading-normal text-frac-black">
          {sections.map((s) => (
            <section
              key={s.id}
              id={s.id}
              className="scroll-mt-28"
              data-testid={`legal-section-${s.id}`}
            >
              <h2 className="text-2xl font-bold text-frac-black">
                {s.heading}
              </h2>
              <div className="mt-4 space-y-4 text-base leading-normal text-frac-gray-70">
                {s.body}
              </div>
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}

export default LegalPage;
